import { testService, type BaselineMeasurementPayload, type SpecimenMeasurementPayload } from './api/testService';
import logger from '@/lib/logger';

const DB_NAME = 'ediphor_labcloud';
const DB_VERSION = 1;
const STORE_NAME = 'upload_queue';
const MAX_RETRIES = 10;
const MAX_RETRY_DELAY = 60000; // 60 seconds

export type MeasurementPhase = 'baseline' | 'specimen';

export interface QueuedUpload {
  id: string; // `${testId}:${phase}`
  testId: string;
  phase: MeasurementPhase;
  payload: BaselineMeasurementPayload | SpecimenMeasurementPayload;
  status: 'pending' | 'retrying' | 'failed';
  retryCount: number;
  maxRetries: number;
  lastAttemptAt: Date | null;
  createdAt: Date;
  errorMessage: string | null;
}

class UploadQueueService {
  private db: IDBDatabase | null = null;
  private retryTimeouts: Map<string, number> = new Map();
  private initialized = false;
  // Single-flight guard: `retryAll()`, `retry()`, a scheduled backoff timer, and a direct
  // `saveWithWriteAhead` call can all independently reach `attemptUpload` for the SAME
  // testId:phase item — e.g. a background retry firing on the `online` event while a fresh
  // save for that same item is also mid-flight. Without this, two concurrent HTTP requests for
  // the same test can race against OTHER test-lifecycle operations server-side (a specimen-save
  // racing a disconnect-recovery cancel produced a corrupted row in production). Keyed by item
  // id so a second concurrent call joins the existing attempt instead of firing a duplicate.
  private inFlightUploads: Map<string, Promise<boolean>> = new Map();

  /**
   * Initialize IndexedDB database
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;

    try {
      this.db = await this.openDatabase();
      this.initialized = true;
      logger.info('[UploadQueue] Initialized successfully');
    } catch (error) {
      logger.error('[UploadQueue] Failed to initialize - write-ahead durability is disabled for this session:', error);
      // Fallback to LocalStorage could be implemented here
    }
  }

  /**
   * Whether write-ahead persistence is actually backed by IndexedDB right now.
   */
  isDurable(): boolean {
    return this.initialized && this.db !== null;
  }

  private openDatabase(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          store.createIndex('status', 'status', { unique: false });
          store.createIndex('lastAttemptAt', 'lastAttemptAt', { unique: false });
        }
      };
    });
  }

  /**
   * Add item to queue and attempt immediate upload
   */
  async enqueue(
    testId: string,
    phase: MeasurementPhase,
    payload: BaselineMeasurementPayload | SpecimenMeasurementPayload
  ): Promise<void> {
    if (!this.initialized) await this.initialize();

    const item: QueuedUpload = {
      id: `${testId}:${phase}`,
      testId,
      phase,
      payload,
      status: 'pending',
      retryCount: 0,
      maxRetries: MAX_RETRIES,
      lastAttemptAt: null,
      createdAt: new Date(),
      errorMessage: null,
    };

    await this.addToQueue(item);
    logger.info(`[UploadQueue] Queued ${phase} for test ${testId}`);

    // Attempt immediate upload
    await this.attemptUpload(item);
  }

  /**
   * Durable-first save. Writes the payload to IndexedDB BEFORE attempting the
   * network upload, guaranteeing the measurement survives a refresh/crash that
   * happens mid-POST (see story-1.1-measurement-write-ahead-persistence).
   *
   * - Success:         item is removed from the queue (via attemptUpload -> dequeue).
   * - Network error:   item stays 'pending' and is retried (backoff + boot/online sweep).
   * - Permanent error: item is marked 'failed' (retained) AND the error is rethrown
   *                    so the caller can surface it.
   */
  async saveWithWriteAhead(
    testId: string,
    phase: MeasurementPhase,
    payload: BaselineMeasurementPayload | SpecimenMeasurementPayload
  ): Promise<void> {
    if (!this.initialized) await this.initialize();

    const item: QueuedUpload = {
      id: `${testId}:${phase}`,
      testId,
      phase,
      payload,
      status: 'pending',
      retryCount: 0,
      maxRetries: MAX_RETRIES,
      lastAttemptAt: null,
      createdAt: new Date(),
      errorMessage: null,
    };

    // WRITE-AHEAD: durable in IndexedDB before any network call is made.
    // updateQueueItem uses put() (upsert), so re-saving the same test/phase
    // overwrites instead of throwing a ConstraintError.
    await this.updateQueueItem(item);
    logger.info(`[UploadQueue] Staged ${phase} for test ${testId} (write-ahead)`);

    // Attempt immediate upload. attemptUpload() dequeues on success, schedules a
    // backoff retry on network error, and marks the item 'failed' on a permanent error.
    const uploaded = await this.attemptUpload(item);

    // Surface permanent (non-network) failures to the caller. attemptUpload mutates
    // item.status synchronously in memory regardless of whether the corresponding
    // DB write actually persists (updateQueueItem no-ops when this.db is null), so
    // reading item.status is reliable even when IndexedDB is unavailable -- unlike
    // a getQueueItem re-read, which returns null whenever this.db is null.
    if (!uploaded && item.status === 'failed') {
      throw new Error(item.errorMessage || `Failed to save ${phase} measurement`);
    }
  }

  /**
   * Remove successfully uploaded item
   */
  async dequeue(testId: string, phase: MeasurementPhase): Promise<void> {
    const id = `${testId}:${phase}`;

    // Clear any pending retry timeout regardless of IndexedDB availability -- attemptUpload
    // can now schedule one even when this.initialized is false.
    const timeoutId = this.retryTimeouts.get(id);
    if (timeoutId) {
      clearTimeout(timeoutId);
      this.retryTimeouts.delete(id);
    }

    if (!this.initialized) return;

    await this.removeFromQueue(id);
    logger.info(`[UploadQueue] Dequeued ${phase} for test ${testId}`);
  }

  /**
   * Retry single item
   */
  async retry(testId: string, phase: MeasurementPhase): Promise<void> {
    if (!this.initialized) return;

    const id = `${testId}:${phase}`;
    const item = await this.getQueueItem(id);

    if (item) {
      await this.attemptUpload(item);
    }
  }

  /**
   * Retry all pending/failed items
   */
  async retryAll(): Promise<void> {
    if (!this.initialized) await this.initialize();

    const items = await this.getAllItems();
    const pendingItems = items.filter((item) => item.status === 'pending' || item.status === 'failed');

    if (pendingItems.length > 0) {
      logger.info(`[UploadQueue] Retrying ${pendingItems.length} queued items`);
      
      for (const item of pendingItems) {
        await this.attemptUpload(item);
        // Small delay between retries to avoid overwhelming the server
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
  }

  /**
   * Get count of pending items
   */
  async getPendingCount(): Promise<number> {
    if (!this.initialized) return 0;

    const items = await this.getAllItems();
    return items.filter((item) => item.status === 'pending' || item.status === 'retrying').length;
  }

  /**
   * Get all queued items
   */
  async getAllItems(): Promise<QueuedUpload[]> {
    if (!this.initialized || !this.db) return [];

    const tx = this.db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const items = await this.promisifyRequest<QueuedUpload[]>(store.getAll());

    // Convert date strings back to Date objects
    return items.map(item => ({
      ...item,
      createdAt: new Date(item.createdAt),
      lastAttemptAt: item.lastAttemptAt ? new Date(item.lastAttemptAt) : null,
    }));
  }

  /**
   * Clear all failed items
   */
  async clearFailed(): Promise<void> {
    if (!this.initialized) return;

    const items = await this.getAllItems();
    const failedItems = items.filter((item) => item.status === 'failed');

    for (const item of failedItems) {
      await this.removeFromQueue(item.id);
    }

    logger.info(`[UploadQueue] Cleared ${failedItems.length} failed items`);
    // TODO: Add toast notification: Cleared ${failedItems.length} failed upload(s)
  }

  /**
   * Export failed items as JSON
   */
  async exportFailed(): Promise<string> {
    const items = await this.getAllItems();
    const failedItems = items.filter((item) => item.status === 'failed');
    return JSON.stringify(failedItems, null, 2);
  }

  /**
   * Attempt to upload an item to the backend. Single-flight: a second concurrent call for the
   * same item id joins the in-progress attempt instead of firing a duplicate HTTP request.
   */
  private attemptUpload(item: QueuedUpload): Promise<boolean> {
    const existing = this.inFlightUploads.get(item.id);
    if (existing) return existing;

    const attempt = this.doAttemptUpload(item).finally(() => {
      this.inFlightUploads.delete(item.id);
    });
    this.inFlightUploads.set(item.id, attempt);
    return attempt;
  }

  private async doAttemptUpload(item: QueuedUpload): Promise<boolean> {
    item.status = 'retrying';
    item.lastAttemptAt = new Date();
    await this.updateQueueItem(item);

    try {
      // Call appropriate API based on phase
      if (item.phase === 'baseline') {
        await testService.saveBaseline(item.testId, item.payload as BaselineMeasurementPayload);
      } else {
        await testService.saveSpecimen(item.testId, item.payload as SpecimenMeasurementPayload);
      }

      // Success - remove from queue
      await this.dequeue(item.testId, item.phase);
      logger.info(`[UploadQueue] ${item.phase === 'baseline' ? 'Baseline' : 'Specimen'} measurement uploaded successfully`);
      // TODO: Add toast notification: ${item.phase} measurement uploaded successfully
      return true;

    } catch (error: any) {
      const isNetworkError = this.isNetworkError(error);

      if (!isNetworkError) {
        // Permanent error (validation, etc.) - mark as failed, don't retry
        item.status = 'failed';
        item.errorMessage = error.message || 'Unknown error';
        await this.updateQueueItem(item);
        logger.error(`[UploadQueue] Permanent error for ${item.id}:`, error);
        return false;
      }

      // Network error - schedule retry with exponential backoff
      item.retryCount++;
      
      if (item.retryCount >= item.maxRetries) {
        // Max retries reached
        item.status = 'failed';
        item.errorMessage = `Max retries (${item.maxRetries}) exceeded`;
        await this.updateQueueItem(item);
        logger.error(`[UploadQueue] Max retries exceeded for ${item.id} - Failed to upload ${item.phase} after ${item.maxRetries} attempts`);
        // TODO: Add toast error notification: Failed to upload ${item.phase} after ${item.maxRetries} attempts
        return false;
      }

      // Schedule retry
      item.status = 'pending';
      item.errorMessage = error.message || 'Network error';
      await this.updateQueueItem(item);

      this.scheduleRetry(item);
      return false;
    }
  }

  /**
   * Schedule retry with exponential backoff
   */
  private scheduleRetry(item: QueuedUpload): void {
    const delay = this.getRetryDelay(item.retryCount);

    logger.debug(`[UploadQueue] Scheduling retry for ${item.id} in ${delay}ms (attempt ${item.retryCount}/${item.maxRetries})`);

    const timeoutId = window.setTimeout(() => {
      this.attemptUpload(item);
    }, delay);

    this.retryTimeouts.set(item.id, timeoutId);
  }

  /**
   * Calculate exponential backoff delay
   */
  private getRetryDelay(retryCount: number): number {
    // 2^n * 1000ms, capped at MAX_RETRY_DELAY
    const delay = Math.min(Math.pow(2, retryCount) * 1000, MAX_RETRY_DELAY);
    return delay;
  }

  /**
   * Check if error is a network error (should queue)
   */
  private isNetworkError(error: any): boolean {
    return (
      error.code === 'ECONNABORTED' ||
      error.code === 'ENOTFOUND' ||
      error.code === 'ENETUNREACH' ||
      error.message?.includes('Network Error') ||
      error.message?.includes('timeout') ||
      !navigator.onLine ||
      (error.response && error.response.status >= 500) // Server errors might be temporary
    );
  }

  // IndexedDB CRUD operations

  private async addToQueue(item: QueuedUpload): Promise<void> {
    if (!this.db) return;

    const tx = this.db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    await this.promisifyRequest(store.add(item));
  }

  private async updateQueueItem(item: QueuedUpload): Promise<void> {
    if (!this.db) return;

    const tx = this.db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    await this.promisifyRequest(store.put(item));
  }

  private async removeFromQueue(id: string): Promise<void> {
    if (!this.db) return;

    const tx = this.db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    await this.promisifyRequest(store.delete(id));
  }

  private async getQueueItem(id: string): Promise<QueuedUpload | null> {
    if (!this.db) return null;

    const tx = this.db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const item = await this.promisifyRequest<QueuedUpload | undefined>(store.get(id));
    
    if (!item) return null;

    // Convert date strings back to Date objects
    return {
      ...item,
      createdAt: new Date(item.createdAt),
      lastAttemptAt: item.lastAttemptAt ? new Date(item.lastAttemptAt) : null,
    };
  }

  private promisifyRequest<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }
}

export const uploadQueueService = new UploadQueueService();